import { useMemo } from 'react';
import { ModuleShell } from '../components/ModuleShell';
import { StatusChip, EmptyState } from '../components/StatusChip';
import type { PipelineResponse, PipelineCard, ModuleState } from '../api/types';
import type { Filters } from '../state/useURLState';

const STAGE_ORDER = ['ICP Complete', 'List Building', 'Copy', 'Sending', 'Live', 'Paused'];
const STAGE_COLORS: Record<string, string> = { 'ICP Complete': 'var(--blue)', 'List Building': 'var(--purple)', 'Copy': 'var(--yellow)', 'Sending': 'var(--green)', 'Live': 'var(--green-bright)', 'Paused': 'var(--text-ghost)' };
function ageVariant(d: number | null): 'success' | 'warning' | 'error' | 'muted' { if (d == null) return 'muted'; if (d > 14) return 'error'; if (d > 7) return 'warning'; return 'success'; }

interface Props { moduleState: ModuleState<PipelineResponse> & { refetch: () => void }; filters: Filters }

export function PipelineModule({ moduleState, filters }: Props) {
  const raw = moduleState.data?.cards || [];
  const cards = useMemo(() => {
    let r = [...raw];
    if (filters.client) r = r.filter(c => c.client_id === filters.client);
    if (filters.stage) r = r.filter(c => c.current_stage === filters.stage);
    if (filters.search) { const q = filters.search.toLowerCase(); r = r.filter(c => (c.client_name || '').toLowerCase().includes(q) || (c.campaign_name || '').toLowerCase().includes(q) || (c.title || '').toLowerCase().includes(q)); }
    return r;
  }, [raw, filters]);

  const stageGroups = useMemo(() => {
    const groups = new Map<string, PipelineCard[]>();
    for (const c of cards) { const s = c.current_stage || 'Unknown'; if (!groups.has(s)) groups.set(s, []); groups.get(s)!.push(c); }
    const ordered: Array<[string, PipelineCard[]]> = [];
    for (const s of STAGE_ORDER) { if (groups.has(s)) { ordered.push([s, groups.get(s)!]); groups.delete(s); } }
    for (const [s, items] of groups) ordered.push([s, items]);
    return ordered;
  }, [cards]);

  return (
    <ModuleShell name="pipeline" title="Campaign Pipeline" status={moduleState.status} error={moduleState.error} dataFreshness={moduleState.dataFreshness} fetchedAt={moduleState.fetchedAt} isStale={moduleState.isStale} truth={moduleState.truth} staleReason={moduleState.staleReason} onRetry={moduleState.refetch}
      headerRight={<StatusChip label={`${cards.length} cards`} variant="muted" />}>
      {cards.length === 0 ? <EmptyState title="No pipeline cards loaded" sub="This may be a known backend issue where the endpoint checks sync_run completion." /> : (
        stageGroups.map(([stage, stageCards]) => {
          const color = STAGE_COLORS[stage] || 'var(--text-ghost)';
          return (
            <div key={stage} style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color }}>{stage}</span>
                <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: color + '20', color }}>{stageCards.length}</span>
              </div>
              {stageCards.map(card => (
                <div key={card.id} style={{ padding: '12px 16px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderLeft: `3px solid ${color}`, borderRadius: '0 var(--radius-md) var(--radius-md) 0', marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{card.campaign_name || card.title || card.id}</span>
                      {card.id && (
                        <a href={`https://www.notion.so/${card.id.replace(/-/g, '')}`} target="_blank" rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{ fontSize: 10, color: 'var(--text-dim)', textDecoration: 'none' }}
                          title="Open in Notion">↗</a>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-ghost)', marginTop: 2 }}>
                      {card.client_name || card.client_id}
                      {card.owner && <> <span style={{ color: 'var(--text-dead)' }}>·</span> {card.owner}</>}
                    </div>
                  </div>
                  {card.days_in_stage != null && <StatusChip label={`${card.days_in_stage}d`} variant={ageVariant(card.days_in_stage)} />}
                </div>
              ))}
            </div>
          );
        })
      )}
    </ModuleShell>
  );
}
